import { rampFactor } from '@/domain/rampCurve';
import type { AcquisitionModel, SimulatorState } from '@/domain/types';

/**
 * Nombre de nouveaux clients acquis au mois donné, selon le modèle de montée en charge.
 *
 * `newClientsPerMonth` est le **rythme de croisière** (plateau). Selon le modèle, ce
 * plateau est atteint immédiatement (`constant`) ou au bout de `rampUpMonths` mois :
 * - `linear-ramp` : apport = plateau × (mois / rampUpMonths), plafonné au plateau.
 * - `s-curve` : apport = plateau × smoothstep(mois / rampUpMonths) — démarrage lent,
 *   accélération, puis plateau (dérivée nulle aux deux bornes).
 *
 * Pure et déterministe. `month` commence à 1 (cf. boucle de `computeProjection`).
 */
export function acquisitionAtMonth(
  month: number,
  acquisition: SimulatorState['acquisition'],
): number {
  const target = acquisition.newClientsPerMonth;
  const model: AcquisitionModel = acquisition.model ?? 'constant';

  if (model === 'constant') return target;

  const ramp = acquisition.rampUpMonths ?? 0;
  // Pas de durée de montée → plateau immédiat (équivalent au modèle constant).
  if (ramp <= 0) return target;

  // Progression normalisée dans [0, 1] ; au-delà de la durée de montée, on est au plateau.
  const t = Math.min(month / ramp, 1);
  return target * rampFactor(t, model);
}
